import { format } from 'date-fns';
import React from 'react';
import { useNotesRangeService } from '../services/notes-range-service';

export const RANGE_ONE = 'FAIXA_1';
export const RANGE_TWO = 'FAIXA_2';
export const RANGE_THREE = 'FAIXA_3';
export const RANGE_FOUR = 'FAIXA_4';
export const RANGE_FIVE = 'FAIXA_5';
export const RANGE_SIX = 'FAIXA_6';

export const LEFT = 'left';
export const RIGHT = 'right';

const DATE_FORMAT = 'dd/MM/yyyy - HH:mm:ss';

export const useNotesRangeController = () => {
  const {
    listNotesRange,
    calibrationHistory,
    calibrationHistoryDetail,
    updateRangeList,
    getLastStatusCalibration,
    listStatusCalibration,
    updateStatusCalibration,
  } = useNotesRangeService();

  const getNotesRange = React.useCallback(async () => {
    try {
      return await listNotesRange();
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [listNotesRange]);

  const getListStatusCalibration = React.useCallback(async () => {
    try {
      const response = await listStatusCalibration();
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [listStatusCalibration]);

  const updateStatusOfCalibration = React.useCallback(async () => {
    try {
      return await updateStatusCalibration();
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [updateStatusCalibration]);

  const getStatusCalibration = React.useCallback(async () => {
    try {
      const status = getLastStatusCalibration();
      return status;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [getLastStatusCalibration]);

  const getHistory = React.useCallback(async () => {
    try {
      const historyList = await calibrationHistory();
      return historyList.map((history) => ({
        ...history,
        date: format(new Date(history.date), DATE_FORMAT),
      }));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [calibrationHistory]);

  const completeWithZero = React.useCallback((num, direction, limit) => {
    if (num === '100') {
      return num;
    }

    if (num?.length < 2) {
      num = '0' + num;
    }

    while (num?.length < limit)
      num = direction === LEFT ? '0' + num : num + '0';
    return num;
  }, []);

  const completeValue = React.useCallback(
    (item) => {
      const regEx = /([^\d])+/gim;
      const values = item.split(',');

      let integerNumber = values[0].replace(regEx, '');
      let fractionalNumber = values[1].replace(regEx, '');

      return completeWithZero(integerNumber, LEFT, 2)
        .concat(',')
        .concat(completeWithZero(fractionalNumber, RIGHT, 2));
    },
    [completeWithZero],
  );

  const applyMask = React.useCallback((value, canApply) => {
    return canApply
      ? value?.substring(0, 2).concat(',').concat(value?.substring(3, 5))
      : value;
  }, []);

  const getHistoryDetail = React.useCallback(
    async (ids) => {
      try {
        const historyDetailList = await calibrationHistoryDetail(ids);
        return historyDetailList.map((historyDetail) => ({
          ...historyDetail,
          start: applyMask(
            completeWithZero(historyDetail.start?.toString(), RIGHT, 5),
            true,
          ),
          end: applyMask(
            completeWithZero(historyDetail.end?.toString(), RIGHT, 5),
            historyDetail.rangeScoreCce.name !== RANGE_SIX,
          ),
        }));
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [applyMask, calibrationHistoryDetail, completeWithZero],
  );

  const parseFloatValue = React.useCallback((value) => {
    return parseFloat(value.toString().replace(',', '.'));
  }, []);

  const checkRange = React.useCallback(
    (range) =>
      range.name === RANGE_ONE || range.name === RANGE_SIX
        ? parseFloatValue(range.start) <= parseFloatValue(range.end)
        : parseFloatValue(range.start) < parseFloatValue(range.end),
    [parseFloatValue],
  );

  const isValidCalibration = React.useCallback(
    (rangeMap) => rangeMap.every((item) => checkRange(item.range)),
    [checkRange],
  );

  const update = React.useCallback(
    async (rangeList) => {
      try {
        return await updateRangeList(
          rangeList.map((range) => ({
            ...range,
            start: parseFloatValue(range.start),
            end: parseFloatValue(range.end),
          })),
        );
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [parseFloatValue, updateRangeList],
  );

  return {
    getNotesRange,
    getHistory,
    getHistoryDetail,
    checkRange,
    isValidCalibration,
    completeWithZero,
    completeValue,
    applyMask,
    update,
    getStatusCalibration,
    getListStatusCalibration,
    updateStatusOfCalibration,
  };
};
